import type { WebRTCConnection } from "../models";
import type {
  GamepadInfo,
  GameSessionStats,
  GPUStats,
  Navigator,
  NetworkInfo,
  RTCConnectionStats,
} from "../types/eonStats";
import { collectDeviceCapabilities } from "../utils/deviceCapabilities";

class SessionStatsService {
  public initialized = false;
  private rtcConnection: WebRTCConnection | null = null;

  public initialize({ rtcConnection }: { rtcConnection: WebRTCConnection }) {
    if (this.initialized) return;

    this.rtcConnection = rtcConnection;
    this.initialized = true;
  }

  public cleanup() {
    this.rtcConnection = null;
    this.initialized = false;
  }

  private getNetworkInfo(): NetworkInfo {
    const connection =
      (navigator as Navigator).connection ||
      (navigator as Navigator).mozConnection ||
      (navigator as Navigator).webkitConnection;

    return {
      effectiveNWType: connection?.effectiveType,
      nwType: connection?.type,
      downlink: connection?.downlink,
      rtt: connection?.rtt,
      networkType: connection?.type,
    };
  }

  private getGpuStats(): GPUStats {
    return {
      gpuLoad: 0,
      gpuMemoryTotal: 0,
      gpuMemoryUsed: 0,
    };
  }

  private getRTCStats(): Promise<RTCConnectionStats> | undefined {
    return this.rtcConnection?.getConnectionStats();
  }

  private getGamepadInfo(): GamepadInfo[] {
    const gamepads = Array.from(navigator.getGamepads()).filter(
      (o) => o !== null,
    );

    return gamepads.map((item) => ({
      id: item.id,
      index: item.index,
      axes: item.axes,
    }));
  }

  public async getGameSessionStats(): Promise<GameSessionStats> {
    const rtcStats = await this.getRTCStats();

    const gpuStats = this.getGpuStats();
    const gamepadInfo = this.getGamepadInfo();
    const deviceInfo = await collectDeviceCapabilities(
      this.rtcConnection?.getVideoElement(),
    );
    const networkInfo = this.getNetworkInfo();

    return {
      rtcStats: rtcStats!,
      gpuStats,
      deviceInfo,
      networkInfo,
      gamepadInfo,
    };
  }
}

export const sessionStatsService = new SessionStatsService();
